import React from "react";
import Reference from "./reference.tsx";
import DetailHistory from "./detailHistory.tsx";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import {
  archivedComplaint,
  getComplaintDetails,
} from "@core/actions/complaintAction.ts";
import {
  complaintDetails,
  loadingArchived,
  loadingDetailsComplaint,
} from "@core/slice/ComplaintSlice.ts";
import { FetchStatusType } from "@core/modeles/model.ts";
import Spinner from "@shared/Spinner.tsx";
import { useParams } from "react-router-dom";
import {
  ComplaintStatus,
  TypeProfil,
} from "@modules/plaintes/plaintes.model.ts";
import ReferencePending from "./referencePending.tsx";
import DetailHistoryPending from "./detailHistoryPending.tsx";
import { userConnected } from "@core/slice/UsersSlice.ts";
import Message from "@shared/message/Message.tsx";
import CustomDialog from "@shared/customDialog.tsx";
import { isDateOlderThan7Days } from "@utils/dateUtils.ts";

const DetailComplaint: React.FC = () => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const id = params.id;
  const complaint = useAppSelector(complaintDetails);
  const loading = useAppSelector(loadingDetailsComplaint);
  const detailsUserConnected = useAppSelector(userConnected);
  const loadingArchive = useAppSelector(loadingArchived);

  const [open, setOpen] = React.useState<boolean>(false);
  const [archived, setArchived] = React.useState<boolean>(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [visibleConfirmDialog, setVisibleConfirmDialog] =
    React.useState<boolean>(false);
  const [messageInfo, setMessageInfo] = React.useState<{
    severity: "success" | "info" | "warn" | "error";
    summary: string;
    detail: string;
  }>({
    severity: "success",
    summary: "",
    detail: "",
  });

  const isExternalAgent =
    detailsUserConnected &&
    detailsUserConnected.profil.type === TypeProfil.EXTERNAL;

  const isInternalAgent =
    detailsUserConnected &&
    detailsUserConnected.profil.accesses[0].internalAgent;

  const isTransfer =
    complaint?.status !== ComplaintStatus.ARCHIVED &&
    detailsUserConnected &&
    (detailsUserConnected.profil.profilId.value ===
      complaint?.complaintUsers[0].profil.id ||
      isExternalAgent);

  const isCgp = detailsUserConnected?.profil.type === TypeProfil.CGP;
  const isPFM = detailsUserConnected?.profil.type === TypeProfil.PFM;

  const transferAllAgent =
    isTransfer &&
    (isInternalAgent ||
      isExternalAgent ||
      isPFM ||
      isCgp ||
      (detailsUserConnected &&
        (detailsUserConnected.profil.type === "ADMIN" ||
          detailsUserConnected.profil.type === TypeProfil.ADMIN_DRPS) &&
        complaint!.status !== ComplaintStatus.RESOLVED_NEGATIVE &&
        complaint!.status !== ComplaintStatus.RESOLVED_POSITIVE) ||
      (detailsUserConnected &&
        detailsUserConnected.profil.type === "RGS" &&
        complaint!.status !== ComplaintStatus.RESOLVED_NEGATIVE &&
        complaint!.status !== ComplaintStatus.RESOLVED_POSITIVE));

  const adminTransferResolved =
    isTransfer &&
    detailsUserConnected &&
    (detailsUserConnected.profil.type === "ADMIN" ||
      detailsUserConnected.profil.type === TypeProfil.ADMIN_DRPS) &&
    (complaint!.status === ComplaintStatus.RESOLVED_NEGATIVE ||
      complaint!.status === ComplaintStatus.RESOLVED_POSITIVE);

  const isNotTransfer =
    !isTransfer &&
    (isInternalAgent ||
      ((isPFM || isCgp) &&
        complaint &&
        complaint.status !== ComplaintStatus.RESOLVED_NEGATIVE &&
        complaint.status !== ComplaintStatus.RESOLVED_POSITIVE) ||
      (detailsUserConnected && detailsUserConnected.profil.type === "RGS") ||
      (detailsUserConnected && detailsUserConnected.profil.type === "ADMIN") ||
      (detailsUserConnected &&
        detailsUserConnected.profil.type === TypeProfil.ADMIN_DRPS));

  const pointFocalResoluNotTransfert =
    !isTransfer &&
    (isPFM || isCgp) &&
    complaint &&
    (complaint.status === ComplaintStatus.RESOLVED_NEGATIVE ||
      complaint.status === ComplaintStatus.RESOLVED_POSITIVE);

  const isFocalMocipArchived =
    (isPFM || isCgp) &&
    (complaint?.status === ComplaintStatus.RESOLVED_NEGATIVE ||
      complaint?.status === ComplaintStatus.RESOLVED_POSITIVE);

  const isMeasuresToBeTaken = complaint?.aggressors.length === 0;

  React.useEffect(() => {
    if (id) {
      const complaintId = parseInt(id);
      dispatch(getComplaintDetails(complaintId));
      setArchived(false);
    }
  }, [dispatch, id, archived]);

  const handleChangeActiveIndex = () => {
    if (activeIndex === 0) setActiveIndex(1);
    else setActiveIndex(0);
  };

  const showSuccessMessage = () => {
    setMessageInfo({
      severity: "success",
      summary: "Succès",
      detail: "La plainte est archivée avec succès.",
    });
    setOpen(true);
  };

  const showErrorMessage = () => {
    setMessageInfo({
      severity: "error",
      summary: "Erreur",
      detail: "Une erreur c'est produite , veuillez réessayer!",
    });
    setOpen(true);
  };

  const confirmArchive = () => {
    if (id)
      dispatch(
        archivedComplaint({
          filter: {
            id: +id,
          },
          onSuccess: () => {
            showSuccessMessage();
            setVisibleConfirmDialog(false);
            setArchived(true);
          },
          onError: () => {
            showErrorMessage();
          },
        })
      );
  };
  const rejectDelete = () => {
    setVisibleConfirmDialog(false);
  };

  return (
    <>
      <Message
        open={open}
        severity={messageInfo.severity}
        summary={messageInfo.summary}
        detail={messageInfo.detail}
      />
      <CustomDialog
        visible={visibleConfirmDialog}
        title="Souhaitez-vous archiver définitivement cette plainte ? Cette action est irréversible."
        rejectLabel="Annuler"
        confirmLabel="Archiver"
        confirmDialog={confirmArchive}
        rejectDialog={rejectDelete}
        loading={loadingArchive === FetchStatusType.LOADING}
        disabled={loadingArchive === FetchStatusType.LOADING}
      />
      <div className="w-full column gap-5 bg-white rounded-lg py-8">
        {loading === FetchStatusType.LOADING || !complaint ? (
          <Spinner />
        ) : (
          <>
            {(isNotTransfer ||
              (isMeasuresToBeTaken && transferAllAgent) ||
              adminTransferResolved ||
              complaint.status === ComplaintStatus.ARCHIVED) && (
              <>
                <Reference complaint={complaint} />
                <DetailHistory
                  complaint={complaint}
                  status={complaint.status}
                  isMeasuresToBeTaken={
                    isMeasuresToBeTaken && (transferAllAgent as boolean)
                  }
                  setVisibleConfirmDialog={setVisibleConfirmDialog}
                  isDateOlderThan7Days={isDateOlderThan7Days}
                />
              </>
            )}
            {(pointFocalResoluNotTransfert ||
              (transferAllAgent && !isMeasuresToBeTaken)) && (
              <>
                <ReferencePending
                  complaint={complaint}
                  activeIndex={activeIndex}
                  isFocalMocipArchived={isFocalMocipArchived}
                />
                <DetailHistoryPending
                  status={complaint.status}
                  complaint={complaint}
                  handleChangeActiveIndex={handleChangeActiveIndex}
                  activeIndex={activeIndex}
                  setMessageInfo={setMessageInfo}
                  setActiveIndex={setActiveIndex}
                  id={id}
                  setOpen={setOpen}
                  isFocalMocipArchived={isFocalMocipArchived}
                  setVisibleConfirmDialog={setVisibleConfirmDialog}
                  isDateOlderThan7Days={isDateOlderThan7Days}
                />
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default DetailComplaint;
